import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getSessionToken } from '../services/sessionTokenService';
import { trackAuthEvent } from '../utils/analytics';
import { isTokenExpired, formatTimeUntilExpiration, getTimeUntilExpiration } from '../utils/jwtUtils';
import { refreshAccessToken, storeRefreshToken, clearRefreshToken, getRefreshToken, shouldRefreshToken } from '../services/tokenRefreshService';

interface User {
  id: number;
  email: string;
  createdAt?: Date;
  hasPassword?: boolean;
  hasGoogleId?: boolean;
  hasProSubscription?: boolean;
  /** When the user first used the 14-day free trial; null/undefined if never used */
  freeTrialUsedAt?: string | null;
  /** When true, user is blocked from subscribing (e.g. abuse pattern) */
  disallowProSubscription?: boolean;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (accessToken: string, user: User, refreshToken?: string) => void;
  logout: () => void;
  refreshUser: () => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Listen for token expiration events from API interceptors
  useEffect(() => {
    const handleTokenExpired = () => {
      console.log('🔍 AuthContext: Received token expiration event, clearing user state');
      setUser(null);
    };

    window.addEventListener('auth:token-expired', handleTokenExpired);
    return () => {
      window.removeEventListener('auth:token-expired', handleTokenExpired);
    };
  }, []);

  useEffect(() => {
    // Check if user is logged in on app start
    const token = localStorage.getItem('accessToken');
    const cachedUser = localStorage.getItem('user');
    
    // Check token expiration before proceeding
    if (token) {
      const expired = isTokenExpired(token);
      const timeUntilExpiration = getTimeUntilExpiration(token);
      const expirationInfo = formatTimeUntilExpiration(token);
      
      console.log('🔍 AuthContext: Checking authentication state:', {
        hasToken: !!token,
        hasCachedUser: !!cachedUser,
        tokenPreview: token ? `${token.substring(0, 20)}...` : null,
        isExpired: expired,
        timeUntilExpiration: timeUntilExpiration !== null ? `${Math.floor((timeUntilExpiration || 0) / (1000 * 60 * 60 * 24))} days` : 'unknown',
        expirationInfo
      });
      
      if (expired) {
        console.warn('⚠️ AuthContext: Token is expired, attempting to refresh...');
        // Try to refresh the token
        refreshAccessToken()
          .then((newTokens) => {
            if (newTokens) {
              console.log('✅ AuthContext: Token refreshed successfully');
              const userData = JSON.parse(localStorage.getItem('user') || '{}');
              setUser(userData);
            } else {
              console.warn('⚠️ AuthContext: Failed to refresh token, clearing authentication data');
              localStorage.removeItem('accessToken');
              localStorage.removeItem('user');
              clearRefreshToken();
              setUser(null);
            }
          })
          .catch((error) => {
            console.error('❌ AuthContext: Error refreshing token:', error);
            localStorage.removeItem('accessToken');
            localStorage.removeItem('user');
            clearRefreshToken();
            setUser(null);
          })
          .finally(() => {
            setLoading(false);
          });
        return;
      }
      
      // Check if token should be refreshed soon (within 1 hour)
      if (shouldRefreshToken(token)) {
        console.log('🔍 AuthContext: Token expiring soon, refreshing in background...');
        // Refresh in background without blocking
        refreshAccessToken()
          .then((newTokens) => {
            if (newTokens) {
              console.log('✅ AuthContext: Token refreshed in background');
              const userData = JSON.parse(localStorage.getItem('user') || '{}');
              setUser(userData);
            }
          })
          .catch((error) => {
            console.warn('⚠️ AuthContext: Background token refresh failed:', error);
          });
      }
    } else {
      console.log('🔍 AuthContext: Checking authentication state:', {
        hasToken: false,
        hasCachedUser: !!cachedUser
      });
    }
    
    if (token && cachedUser) {
      // If we have both token and cached user data, use cached data immediately
      // This prevents the loading flash during navigation
      try {
        const userData = JSON.parse(cachedUser);
        console.log('🔍 AuthContext: Setting user from cache:', userData);
        setUser(userData);
        setLoading(false);
        
        // Optionally verify token in background (without showing loading)
        console.log('🔍 AuthContext: Starting background verification...');
        const sessionToken = getSessionToken();
        const headers: Record<string, string> = {
          'Authorization': `Bearer ${token}`,
        };
        if (sessionToken) {
          headers['X-Session-Token'] = sessionToken;
        }
        fetch('/api/v1/auth/profile', {
          headers,
        })
          .then(response => {
            console.log('🔍 AuthContext: Background verification response:', response.status);
            if (response.ok) {
              return response.json();
            }
            throw new Error(`Token invalid: ${response.status}`);
          })
          .then(userData => {
            console.log('✅ AuthContext: Background verification successful:', userData);
            // Update cached user data if verification succeeds
            localStorage.setItem('user', JSON.stringify(userData));
            setUser(userData);
          })
          .catch(async (error) => {
            console.log('❌ AuthContext: Background verification failed:', error.message);
            
            // Only clear authentication if it's a real authentication error (401, 403)
            // Don't logout on rate limits (429) or other temporary errors
            if (error.message.includes('401') || error.message.includes('403')) {
              console.warn('⚠️ AuthContext: Authentication error detected (401/403), attempting to refresh token...');
              console.log('🔍 AuthContext: Token expiration info:', token ? formatTimeUntilExpiration(token) : 'No token');
              
              // Try to refresh the token
              const newTokens = await refreshAccessToken();
              if (newTokens) {
                console.log('✅ AuthContext: Token refreshed successfully after verification failure');
                const userData = JSON.parse(localStorage.getItem('user') || '{}');
                setUser(userData);
              } else {
                console.warn('⚠️ AuthContext: Failed to refresh token, logging out user');
                localStorage.removeItem('accessToken');
                localStorage.removeItem('user');
                clearRefreshToken();
                setUser(null);
              }
            } else {
              console.log('🔍 AuthContext: Non-auth error (likely rate limit or network issue), keeping user logged in');
              // Keep user logged in for rate limits and other temporary errors
            }
          });
      } catch (error) {
        console.log('❌ AuthContext: Invalid cached data:', error);
        // Invalid cached data, clear it
        localStorage.removeItem('user');
        setLoading(false);
      }
    } else if (token) {
      console.log('🔍 AuthContext: Token exists but no cached user, verifying...');
      // We have a token but no cached user data - verify with backend
      const sessionToken = getSessionToken();
      const headers: Record<string, string> = {
        'Authorization': `Bearer ${token}`,
      };
      if (sessionToken) {
        headers['X-Session-Token'] = sessionToken;
      }
      fetch('/api/v1/auth/profile', {
        headers,
      })
        .then(response => {
          console.log('🔍 AuthContext: Token verification response:', response.status);
          if (response.ok) {
            return response.json();
          }
          throw new Error(`Token invalid: ${response.status}`);
        })
        .then(userData => {
          console.log('✅ AuthContext: Token verification successful:', userData);
          setUser(userData);
          localStorage.setItem('user', JSON.stringify(userData));
        })
        .catch(async (error) => {
          console.log('❌ AuthContext: Token verification failed:', error.message);
          
          // Only clear token if it's a real authentication error (401, 403)
          // Don't logout on rate limits (429) or other temporary errors
          if (error.message.includes('401') || error.message.includes('403')) {
            console.warn('⚠️ AuthContext: Authentication error detected (401/403), attempting to refresh token...');
            console.log('🔍 AuthContext: Token expiration info:', token ? formatTimeUntilExpiration(token) : 'No token');
            
            // Try to refresh the token
            const newTokens = await refreshAccessToken();
            if (newTokens) {
              console.log('✅ AuthContext: Token refreshed successfully');
              const userData = JSON.parse(localStorage.getItem('user') || '{}');
              setUser(userData);
            } else {
              console.warn('⚠️ AuthContext: Failed to refresh token, clearing authentication');
              localStorage.removeItem('accessToken');
              localStorage.removeItem('user');
              clearRefreshToken();
              setUser(null);
            }
          } else {
            console.log('🔍 AuthContext: Non-auth error (likely rate limit or network issue), keeping token');
            // Keep token for rate limits and other temporary errors
          }
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      console.log('🔍 AuthContext: No token found, user not authenticated');
      // Safety check: if there's cached user data but no token, clear it
      if (cachedUser) {
        console.log('🔍 AuthContext: Found cached user data without token, clearing it');
        localStorage.removeItem('user');
      }
      setLoading(false);
    }
  }, []);

  const login = (accessToken: string, userData: User, refreshToken?: string) => {
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    
    // Store refresh token if provided
    if (refreshToken) {
      storeRefreshToken(refreshToken);
    }
    
    // Track login event
    trackAuthEvent('login');
    
    // Clear any cached list data to prevent using old list IDs
    localStorage.removeItem('stocktable-user-lists-cache');
    localStorage.removeItem('stocktable-current-list-id');
    localStorage.removeItem('stocktable-current-list-name');
    
    // Clear any cached list data for specific lists
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('stocktable-list-') && key.endsWith('-cache')) {
        localStorage.removeItem(key);
      }
    });
    
    console.log('🔍 Login: Cleared cached list data to prevent stale list IDs');
  };

  const logout = () => {
    console.log('🔍 Logout: Clearing authentication data');
    
    // Track logout event
    trackAuthEvent('logout');
    
    // Clear authentication data
    localStorage.removeItem('accessToken');
    localStorage.removeItem('user');
    clearRefreshToken();
    setUser(null);
    
    // Clear session data (ticker lists and preferences)
    sessionStorage.removeItem('stocktable-ticker-list');
    sessionStorage.removeItem('stocktable-list-name');
    sessionStorage.removeItem('stocktable-hidden-statistics');
    sessionStorage.removeItem('stocktable-preferences');
    
    // Clear cached list data
    localStorage.removeItem('stocktable-user-lists-cache');
    localStorage.removeItem('stocktable-current-list-id');
    localStorage.removeItem('stocktable-current-list-name');
    
    // Clear any cached list data for specific lists
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('stocktable-list-') && key.endsWith('-cache')) {
        localStorage.removeItem(key);
      }
    });
    
    console.log('✅ Logout: Authentication data and cached list data cleared');
    
    // Note: Navigation will be handled by the component that calls logout
  };

  const refreshUser = async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) return;
    const sessionToken = getSessionToken();
    const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
    if (sessionToken) headers['X-Session-Token'] = sessionToken;
    const res = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/v1/auth/profile`, { headers });
    if (!res.ok) return;
    const userData = await res.json();
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const value = {
    user,
    isAuthenticated: !!user,
    login,
    logout,
    refreshUser,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
